import { useEffect } from 'react'

/**
 * useContentGuard — lightweight deterrent against casual image scraping.
 *
 * IMPORTANT: This is NOT real security. Anyone technical can still view the
 * source. It only discourages casual users from right-clicking to save
 * before/after photography or dragging assets off the page.
 *
 * DELIBERATELY NOT BLOCKED (these hurt real patients and cost conversions):
 *   - Text selection. Patients need to copy the phone number, the address,
 *     and the support email. Blocking selection makes a page feel broken.
 *   - Keyboard shortcuts. Blocking Ctrl+S / Ctrl+U / F12 stops nobody who
 *     matters and breaks accessibility tooling and browser translation.
 *
 * Right-click stays enabled on links and form fields so patients can still
 * open the privacy policy in a new tab or use spellcheck while typing.
 */
export function useContentGuard() {
  useEffect(() => {
    const isInteractive = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null
      if (!node || !node.closest) return false
      return !!node.closest(
        'input, textarea, select, a, button, iframe, [contenteditable="true"]',
      )
    }

    // Block the context menu only over media, so photography is harder to grab
    const onContextMenu = (e: MouseEvent) => {
      if (isInteractive(e.target)) return
      const node = e.target as HTMLElement | null
      if (node && node.closest('img, picture, video, svg')) {
        e.preventDefault()
      }
    }

    // Block dragging images out of the page
    const onDragStart = (e: DragEvent) => {
      const node = e.target as HTMLElement | null
      if (node && node.closest('img, picture, video')) {
        e.preventDefault()
      }
    }

    document.addEventListener('contextmenu', onContextMenu)
    document.addEventListener('dragstart', onDragStart)

    return () => {
      document.removeEventListener('contextmenu', onContextMenu)
      document.removeEventListener('dragstart', onDragStart)
    }
  }, [])
}
